import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { apiCall } from '../api/client';

// The signed-in user's BeeFluent account (/me): profile, settings, hive state.
// Fetched once when a token arrives and shared app-wide so pages don't each
// refetch it. Pages that change the account (Settings, SetupUsername) push the
// server's response back in via setUser — merging into the previous value where
// needed, since PATCH /me returns the *stored* hiveState, not the computed one.
// refreshUser refetches on demand (e.g. the dashboard updating hive counts
// after study sessions).
const UserContext = createContext();

export function UserProvider({ children }) {
  const { token, isLoggedIn, isGuest } = useAuth();
  const [user, setUser] = useState(null);
  const [userError, setUserError] = useState(null);

  const refreshUser = useCallback(async () => {
    const data = await apiCall('GET', '/me', null, token);
    setUser(data);
    return data;
  }, [token]);

  useEffect(() => {
    // Guests have no account; skip the (auth-gated) /me lookup entirely.
    if (isGuest || !isLoggedIn || !token) {
      setUser(null);
      return;
    }
    // Only this initial load surfaces an error (App shows a sign-in failure
    // screen for it); later refreshes keep the last good user instead.
    setUserError(null);
    refreshUser().catch(setUserError);
  }, [isLoggedIn, token, isGuest, refreshUser]);

  return (
    <UserContext.Provider value={{ user, userError, setUser, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
